<template>
  <div class="receipt sale-receipt">
    <div v-if="businessInfo" class="receipt-hero q-mb-lg">
      <div
        v-if="(businessInfo.logo_url || businessInfo.logo_original_url) && (businessInfo.logo_url || businessInfo.logo_original_url) !== './src/assets/default-business.png'"
        class="receipt-logo q-mb-md"
      >
        <q-img :src="businessInfo.logo_url || businessInfo.logo_original_url" fit="contain" class="receipt-logo__img" />
      </div>
      <div class="text-h6 text-weight-medium">{{ businessInfo.name }}</div>
      <div v-if="businessInfo.address" class="text-body2 text-grey-8">{{ businessInfo.address }}</div>
      <div v-if="businessInfo.phone" class="text-body2 text-grey-8">
        {{ t('sales.saleReceiptBusinessPhone') }}: {{ businessInfo.phone }}
      </div>
      <div v-if="businessInfo.email" class="text-body2 text-grey-8">
        {{ t('sales.saleReceiptBusinessEmail') }}: {{ businessInfo.email }}
      </div>
      <div v-if="businessInfo.tax_id" class="text-body2 text-grey-8">
        {{ t('sales.saleReceiptTaxId') }}: {{ businessInfo.tax_id }}
      </div>
    </div>
    <q-separator class="q-mb-lg" />

    <div class="row q-col-gutter-lg">
      <div :class="showCustomerContactPanel ? 'col-12 col-md-6' : 'col-12'">
        <q-card flat bordered class="receipt-panel receipt-panel--tint receipt-panel--sale-details q-mb-md">
          <q-card-section class="receipt-panel__title text-subtitle2 text-weight-medium q-pb-sm">
            {{ t('sales.saleReceiptSaleDetails') }}
          </q-card-section>
          <q-card-section class="q-pt-none">
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptDate') }}</div>
              <div class="col">{{ formatDate(sale.created_at) }}</div>
            </div>
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptSaleNumber') }}</div>
              <div class="col">{{ sale.id }}</div>
            </div>
            <div v-if="usesTabPaymentReceiptLayout" class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptType') }}</div>
              <div class="col">{{ t('sales.tabPaymentReceipt.receiptTitle') }}</div>
            </div>
            <div v-else-if="saleHasInvoicePaymentReceiptItems(sale)" class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptType') }}</div>
              <div class="col">{{ t('sales.invoicePaymentReceipt.receiptTitle') }}</div>
            </div>
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptCustomerLabel') }}</div>
              <div class="col">{{ saleCustomerReceiptLabel }}</div>
            </div>
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptPaymentMethod') }}</div>
              <div class="col">
                <PaymentMethodChip
                  :method="sale.payment_method"
                  :tab-purpose="resolveSaleTabPurpose(sale)"
                  size="sm"
                />
              </div>
            </div>
            <div v-if="sale.sale_source && sale.sale_source !== 'pos'" class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-5 text-grey-7">{{ t('sales.saleReceiptSaleSource') }}</div>
              <div class="col">{{ formatSaleSource(t, sale.sale_source) }}</div>
            </div>
          </q-card-section>
        </q-card>
      </div>
      <div v-if="showCustomerContactPanel" class="col-12 col-md-6">
        <q-card flat bordered class="receipt-panel receipt-panel--tint receipt-panel--sale-details q-mb-md">
          <q-card-section class="receipt-panel__title text-subtitle2 text-weight-medium q-pb-sm">
            {{ t('sales.saleReceiptCustomerDetails') }}
          </q-card-section>
          <q-card-section class="q-pt-none">
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-3 text-grey-7">{{ t('sales.saleReceiptEmailLabel') }}</div>
              <div class="col">{{ saleCustomerDisplay.email || '—' }}</div>
            </div>
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-3 text-grey-7">{{ t('sales.saleReceiptPhoneLabel') }}</div>
              <div class="col">{{ saleCustomerDisplay.phone || '—' }}</div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <q-separator class="q-mb-lg" />

    <SaleTabPaymentSummary
      v-if="sale.tab_payment_context && usesTabPaymentReceiptLayout"
      :context="sale.tab_payment_context"
      :sale="sale"
      :currency="sale.tab_payment_context.currency || 'USD'"
    />

    <template v-if="!usesTabPaymentReceiptLayout">
    <div class="text-subtitle1 text-weight-medium q-mb-md">
      {{ itemsSectionTitle }}
    </div>
    <q-table
      :rows="receiptItems"
      :columns="columns"
      :row-key="receiptItemRowKey"
      flat
      bordered
      class="receipt-items-table"
      wrap-cells
      hide-pagination
      hide-bottom
      :row-class="(row) => row.is_tab_entry ? 'tab-entry-row' : (row.is_tab_payment ? 'tab-payment-row' : (row.is_event_registration ? 'event-row' : (row.redeeming_for_points ? 'redemption-row' : '')))"
    >
      <template v-slot:body-cell-name="props">
        <q-td :props="props">
          <div class="text-weight-medium">
            {{ props.value }}
          </div>
          <div
            v-if="props.row.store_variant_options_label"
            class="text-caption text-grey-7 q-mt-xs"
          >
            {{ props.row.store_variant_options_label }}
          </div>
          <div
            v-if="Array.isArray(props.row.modifiers) && props.row.modifiers.length"
            class="q-mt-xs"
          >
            <div
              v-for="(m, idx) in props.row.modifiers"
              :key="m.id || m.modifier_id || idx"
              class="text-caption text-grey-7 row items-center justify-between"
              style="gap: 12px;"
            >
              <div>
                - {{ m.name_snapshot || m.name || 'Modifier' }}
              </div>
              <div class="text-weight-medium">
                <span v-if="Number(m.price_delta_snapshot || 0) >= 0">+</span>${{ Number(m.price_delta_snapshot || 0).toFixed(2) }}
              </div>
            </div>
          </div>
        </q-td>
      </template>
      <template v-slot:body-cell-discount="props">
        <q-td :props="props">
          <span v-if="props.row.redeeming_for_points">-</span>
          <span v-else-if="props.row.discount_amount > 0">
            <span v-if="props.row.discount_type === 'percentage'">
              {{ formatQuantityDisplay(props.row.discount_amount, { allowFractional: true }) }}%
            </span>
            <span v-else>${{ Number(props.row.discount_amount).toFixed(2) }}</span>
          </span>
          <span v-else>-</span>
        </q-td>
      </template>
      <template v-slot:body-cell-line_total="props">
        <q-td :props="props">
          <span v-if="props.row.redeeming_for_points" class="text-deep-orange">
            {{ props.row.total_redemption_cost || 0 }} pts
          </span>
          <span v-else>
            {{ props.value }}
          </span>
        </q-td>
      </template>
    </q-table>

    <SaleTabPaymentSummary
      v-if="sale.tab_payment_context && !usesTabPaymentReceiptLayout"
      :context="sale.tab_payment_context"
      :currency="sale.tab_payment_context.currency || 'USD'"
    />

    <div class="row justify-center q-mt-lg">
      <div class="col-12 col-sm-10 col-md-7">
        <q-card flat bordered class="receipt-totals">
          <q-card-section class="q-py-md">
            <div class="row q-col-gutter-sm receipt-kv">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptSubtotal') }}</div>
              <div class="col-6 text-right text-weight-medium">
                ${{ Number(sale.base_subtotal || sale.subtotal || 0).toFixed(2) }}
              </div>
            </div>
            <div v-if="sale.discount_amount > 0" class="row q-col-gutter-sm receipt-kv q-pt-xs">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptSaleDiscount') }}</div>
              <div class="col-6 text-right">
                <span v-if="sale.discount_type === 'percentage'">
                  {{ formatQuantityDisplay(sale.discount_amount, { allowFractional: true }) }}%
                </span>
                <span v-else>${{ Number(sale.discount_amount).toFixed(2) }}</span>
              </div>
            </div>
            <div v-if="Number(sale.discount_amount || 0) > 0" class="row q-col-gutter-sm receipt-kv q-pt-xs">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptDiscountedSubtotal') }}</div>
              <div class="col-6 text-right text-weight-medium">${{ Number(sale.subtotal || 0).toFixed(2) }}</div>
            </div>
            <div
              v-for="(line, idx) in receiptTaxLines"
              :key="`tax-line-${idx}`"
              class="row q-col-gutter-sm receipt-kv q-pt-xs"
            >
              <div class="col-6 text-grey-7">{{ line.label }}</div>
              <div class="col-6 text-right">${{ Number(line.amount || 0).toFixed(2) }}</div>
            </div>
            <div v-if="Number(sale.tip_total || 0) > 0" class="row q-col-gutter-sm receipt-kv q-pt-xs">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptTip') }}</div>
              <div class="col-6 text-right">${{ Number(sale.tip_total || 0).toFixed(2) }}</div>
            </div>
            <div v-if="Number(sale.convenience_fee_total || 0) > 0" class="row q-col-gutter-sm receipt-kv q-pt-xs">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptConvenienceFee') }}</div>
              <div class="col-6 text-right">${{ Number(sale.convenience_fee_total || 0).toFixed(2) }}</div>
            </div>
            <div
              v-if="showCashRoundingOnReceipt"
              class="row q-col-gutter-sm receipt-kv q-pt-xs"
            >
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptPennyRounding') }}</div>
              <div class="col-6 text-right">{{ cashRoundingAdjustmentDisplay }}</div>
            </div>
            <q-separator class="q-my-sm" />
            <div class="row q-col-gutter-sm items-center">
              <div class="col-6 text-h6">
                {{ showCashRoundingOnReceipt ? t('sales.saleReceiptCashTotal') : t('sales.saleReceiptTotal') }}
              </div>
              <div class="col-6 text-right text-h6 text-primary">${{ receiptDisplayTotal.toFixed(2) }}</div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
    </template>

    <q-separator v-if="usesTabPaymentReceiptLayout" class="q-my-lg" />

    <div class="text-subtitle1 text-weight-medium q-mb-md">{{ t('sales.saleReceiptPaymentDetails') }}</div>
    <div v-if="sale.payment_method === 'cash' && primaryCashPayment" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptAmountReceived') }}</div>
      <div class="col">${{ parseFloat(primaryCashPayment.amount_received || 0).toFixed(2) }}</div>
    </div>
    <div v-if="sale.payment_method === 'cash' && primaryCashPayment" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptChangeGiven') }}</div>
      <div class="col">${{ parseFloat(primaryCashPayment.change_given || 0).toFixed(2) }}</div>
    </div>
    <div v-if="showKlarnaPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('payments.gateway.colGateway') }}</div>
      <div class="col">{{ t('sales.paymentKlarna') }}</div>
    </div>
    <div v-if="showKlarnaPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('payments.gateway.colStatus') }}</div>
      <div class="col">{{ primaryKlarnaPayment.payment_transaction?.status || 'N/A' }}</div>
    </div>
    <div v-if="showKlarnaPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptTransactionId') }}</div>
      <div class="col">{{ primaryKlarnaPayment.payment_transaction?.reference || 'N/A' }}</div>
    </div>
    <div v-if="showCardPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptCardType') }}</div>
      <div class="col">{{ primaryCardPayment.payment_transaction?.card_brand || 'N/A' }}</div>
    </div>
    <div v-if="showCardPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptCardNumber') }}</div>
      <div class="col">**** **** **** {{ primaryCardPayment.payment_transaction?.card_last4 || '****' }}</div>
    </div>
    <div v-if="showCardPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptAuthorization') }}</div>
      <div class="col">{{ primaryCardPayment.payment_transaction?.auth_code || 'N/A' }}</div>
    </div>
    <div v-if="showCardPaymentDetails" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptTransactionId') }}</div>
      <div class="col">{{ primaryCardPayment.payment_transaction?.reference || 'N/A' }}</div>
    </div>
    <div v-if="showCardPaymentDetails && Number(sale.convenience_fee_total || 0) > 0" class="row q-col-gutter-sm receipt-kv">
      <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptConvenienceFee') }}</div>
      <div class="col">${{ Number(sale.convenience_fee_total || 0).toFixed(2) }}</div>
    </div>
    <div v-if="sale.payment_method === 'split'" class="receipt-payment-split">
      <div
        v-for="(payment, index) in receiptVisiblePayments"
        :key="payment.id || index"
        :class="{ 'q-mt-md q-pt-md receipt-payment-split__leg': index > 0 }"
      >
        <div
          v-if="receiptVisiblePayments.length > 1"
          class="text-body2 text-weight-medium q-mb-sm"
        >
          {{ getReceiptPaymentLabel(payment) }}
          <span class="text-weight-regular text-grey-8">
            · ${{ parseFloat(payment.amount).toFixed(2) }}
          </span>
        </div>
        <template v-if="isCashTenderPayment(payment)">
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptAmountReceived') }}</div>
            <div class="col">${{ parseFloat(payment.amount_received || 0).toFixed(2) }}</div>
          </div>
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptChangeGiven') }}</div>
            <div class="col">${{ parseFloat(payment.change_given || 0).toFixed(2) }}</div>
          </div>
        </template>
        <template v-else-if="isKlarnaLikePayment(payment)">
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('payments.gateway.colGateway') }}</div>
            <div class="col">{{ t('sales.paymentKlarna') }}</div>
          </div>
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptTransactionId') }}</div>
            <div class="col">{{ payment.payment_transaction?.reference || 'N/A' }}</div>
          </div>
        </template>
        <template v-else-if="isCardLikePayment(payment)">
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptCardType') }}</div>
            <div class="col">{{ payment.payment_transaction?.card_brand || 'N/A' }}</div>
          </div>
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptCardNumber') }}</div>
            <div class="col">**** **** **** {{ payment.payment_transaction?.card_last4 || '****' }}</div>
          </div>
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptAuthorization') }}</div>
            <div class="col">{{ payment.payment_transaction?.auth_code || 'N/A' }}</div>
          </div>
          <div class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptTransactionId') }}</div>
            <div class="col">{{ payment.payment_transaction?.reference || 'N/A' }}</div>
          </div>
          <div v-if="Number(payment.fee_amount || 0) > 0" class="row q-col-gutter-sm receipt-kv">
            <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptConvenienceFee') }}</div>
            <div class="col">${{ Number(payment.fee_amount || 0).toFixed(2) }}</div>
          </div>
        </template>
      </div>
      <div v-if="splitConvenienceFeeTotal > 0" class="row q-col-gutter-sm receipt-kv q-mt-md">
        <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptCfTotal') }}</div>
        <div class="col">${{ splitConvenienceFeeTotal.toFixed(2) }}</div>
      </div>
    </div>

    <!-- Payment Actions (Void/Refund per captured card payment only) -->
    <div class="q-mt-lg print-hide" v-if="refundableCardPayments.length > 0">
      <q-separator class="q-mb-md" />
      <div class="text-subtitle1 text-weight-medium q-mb-md">{{ t('sales.saleReceiptActions') }}</div>
      <div v-for="(payment, idx) in refundableCardPayments" :key="payment.id || idx" class="row items-center q-col-gutter-sm q-mb-md">
        <div class="col-12 col-md-6">
          <div class="text-body2 text-weight-medium">
            {{ formatRefundablePaymentSummary(payment) }}
          </div>
        </div>
        <div class="col-12 col-md-6 row justify-end items-center">
          <q-btn
            :label="getPaymentActionLabel(payment)"
            :color="isPaymentSameDay(payment) ? 'warning' : 'negative'"
            icon="undo"
            outline
            dense
            @click="confirmRefundOrVoid(payment)"
          />
        </div>
      </div>
    </div>

    <!-- Loyalty Program Section -->
    <div v-if="showLoyaltySection" class="q-mt-lg">
      <q-separator class="q-mb-lg" />
      <div class="text-subtitle1 text-weight-medium q-mb-md">
        <q-icon name="loyalty" color="primary" class="q-mr-xs" aria-hidden="true" />
        {{ t('sales.saleReceiptLoyalty') }}
      </div>
      <div v-if="showPrevBalance" class="row q-col-gutter-sm receipt-kv">
        <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptPrevBalance') }}</div>
        <div class="col text-weight-medium">{{ prevBalance }} points</div>
      </div>
      <div v-if="earnedPoints > 0 || deltaPositive" class="row q-col-gutter-sm receipt-kv">
        <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptPointsEarned') }}</div>
        <div class="col text-positive">+{{ earnedDisplay }} points<span v-if="isAstro" class="text-caption text-grey-7"> (Astro)</span></div>
      </div>
      <div v-if="redeemedPoints > 0 || totalRedemptionPoints > 0" class="row q-col-gutter-sm receipt-kv">
        <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptPointsRedeemed') }}</div>
        <div class="col text-negative">-{{ redeemedPoints || totalRedemptionPoints }} points<span v-if="isAstro" class="text-caption text-grey-7"> (Astro)</span></div>
      </div>

      <div v-if="redemptionItems.length > 0" class="row q-col-gutter-sm q-mt-sm">
        <div class="col-12">
          <div class="text-caption text-grey-7 q-mb-xs">{{ t('sales.saleReceiptItemsRedeemed') }}</div>
          <div v-for="item in redemptionItems" :key="item.line_item_id" class="text-body2 receipt-redeemed-line">
            • {{ formatQuantityDisplay(item.quantity, { allowFractional: item.product?.allow_fractional ?? null }) }}× {{ item.name || item.product?.name }} ({{ parseInt(item.total_redemption_cost) || 0 }} pts)
          </div>
        </div>
      </div>

      <div v-if="sale.customer && (earnedPoints > 0 || redeemedPoints > 0 || deltaPresent)" class="row q-col-gutter-sm receipt-kv q-mt-sm">
        <div class="col-5 col-sm-4 text-grey-7">{{ t('sales.saleReceiptNewBalance') }}</div>
        <div class="col text-weight-medium">{{ endBalance }} points</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useBusinessStore } from '@/stores/business'
import { useIntegrationsStore } from '@/stores/integrations'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { Notify } from 'quasar'
import { useBluDialog } from '@/composables/useBluDialog'
import { useI18n } from 'vue-i18n'
import { paymentService } from '@/services'
import { integrationService as IntegrationService } from '@/services'
import { useDateFormatter } from '@/composables/useDateFormatter'
import { formatQuantityDisplay } from '@/composables/useQuantity'
import SaleTabPaymentSummary from '@/components/SaleTabPaymentSummary.vue'
import PaymentMethodChip from '@/components/common/PaymentMethodChip.vue'
import { tabEntryReceiptLineItemsFromContext } from '@/utils/tabEntryReceiptItems'
import { invoicePaymentReceiptLineItemsFromContext, saleHasInvoicePaymentReceiptItems } from '@/utils/invoicePaymentReceiptItems'
import { isStructuredTabPaymentContext } from '@/utils/thermalReceiptVolcoraTabPayment'
import {
  formatSalePaymentRowLabel,
  formatSaleSource,
  resolveSaleTabPurpose,
  getSalePaymentChargedTotal,
  isCardLikePayment,
  shouldShowCardPaymentDetails,
  isKlarnaLikePayment,
} from '@/utils/saleDisplay'
import {
  resolveSaleCustomerDisplay,
  resolveSaleCustomerReceiptLabel,
  saleHasAttachedCustomer
} from '@/utils/saleCustomerDisplay'
import { buildReceiptTaxDisplayLines } from '@/utils/receiptTaxDisplay'
import {
  getReceiptDisplayTotal,
  getSaleCashRoundingAdjustment,
  shouldShowCashRoundingOnReceipt
} from '@/utils/receiptCashRoundingDisplay'
import { formatCashRoundingAdjustment } from '@/utils/cashPennyRounding'
import {
  formatUnitPriceCurrency,
  UNIT_DECIMALS_DEFAULT,
  UNIT_DECIMALS_EXTENDED
} from '@/utils/moneyScale'

const { t } = useI18n()

const getReceiptPaymentLabel = (payment) => formatSalePaymentRowLabel(t, payment)

function formatRefundablePaymentSummary (payment) {
  const amount = getSalePaymentChargedTotal(payment).toFixed(2)
  const label = getReceiptPaymentLabel(payment)
  const pt = payment?.payment_transaction
  const last4 = pt?.card_last4
  if (last4) {
    const brand = pt?.card_brand || t('sales.paymentCard')
    return `${label} · ${brand} ····${last4} · $${amount}`
  }
  return `${label} · $${amount}`
}

/** Match backend SalePayment::isVisibleOnReceipt (hide failed / in-flight attempts). */
const HIDDEN_ON_RECEIPT_STATUSES = new Set(['failed', 'declined', 'pending', 'processing'])

function isCashTenderPayment (payment) {
  if (!payment) return false
  if (payment.payment_method === 'cash') return true
  const type = String(payment.payment_method_type || '').toLowerCase()
  return payment.payment_method === 'split' && type === 'cash'
}

function isReceiptPaymentVisible (payment) {
  if (!payment) return false
  const s = String(payment.status ?? '').toLowerCase()
  return !HIDDEN_ON_RECEIPT_STATUSES.has(s)
}

function isPaymentStatusSuccessful (payment) {
  const s = String(payment?.status ?? '').toLowerCase()
  return s === 'succeeded' || s === 'settled'
}

function isPaymentRefundedRecord (payment) {
  if (!payment) return false
  if (payment.refunded_at) return true
  return String(payment.status ?? '').toLowerCase() === 'refunded'
}

/** Match backend SalePayment::isEligibleForProcessorRefund + card-like UI. */
function isEligibleForProcessorRefund (payment) {
  if (!isReceiptPaymentVisible(payment)) return false
  if (!isPaymentStatusSuccessful(payment)) return false
  if (isPaymentRefundedRecord(payment)) return false
  const goods = Number(payment.amount || 0)
  const refunded = Number(payment.amount_refunded || 0)
  if (goods > 0 && refunded >= goods) return false
  if (!payment.payment_transaction_id && !payment.payment_intent_id) return false
  return isCardLikePayment(payment)
}

const isAstro = computed(() => (sale.loyalty_source || '').toLowerCase() === 'astro')
const earnedPoints = computed(() => isAstro.value ? (sale.loyalty_points_earned_external || 0) : (sale.loyalty_points_earned || 0))
const redeemedPoints = computed(() => isAstro.value ? (sale.loyalty_points_redeemed_external || 0) : (sale.loyalty_points_redeemed || 0))
const showLoyaltySection = computed(() => {
  return (earnedPoints.value > 0) || (redeemedPoints.value > 0) || (totalRedemptionPoints.value > 0)
})
const astroBalance = ref(null)
const endBalance = computed(() => {
  if (typeof sale.loyalty_balance_after === 'number') return sale.loyalty_balance_after
  if (isAstro.value) return (astroBalance.value !== null && astroBalance.value !== undefined) ? astroBalance.value : (sale.customer?.loyalty_points || 0)
  return sale.customer?.loyalty_points || 0
})
const prevBalance = computed(() => {
  if (typeof sale.loyalty_balance_before === 'number') return sale.loyalty_balance_before
  return sale.customer?.previous_loyalty_points ?? null
})
const deltaPresent = computed(() => typeof sale.loyalty_balance_delta === 'number')
const deltaPositive = computed(() => (sale.loyalty_balance_delta || 0) > 0)
const earnedDisplay = computed(() => {
  if (typeof sale.loyalty_balance_delta === 'number' && sale.loyalty_balance_delta > 0) return sale.loyalty_balance_delta
  return earnedPoints.value
})
const showPrevBalance = computed(() => prevBalance.value !== null && (earnedPoints.value > 0 || redeemedPoints.value > 0 || totalRedemptionPoints.value > 0 || deltaPresent.value))

const businessStore = useBusinessStore()
const integrationsStore = useIntegrationsStore()
const { info: businessInfo } = storeToRefs(businessStore)
const { confirm: bluDialog } = useBluDialog()

// Fetch business info when component is mounted
onMounted(async () => {
  if (!businessInfo.value) {
    await businessStore.fetchInfo()
  }
  // If Astro is the loyalty source, fetch the current Astro balance for accurate receipt display
  try {
    if (isAstro.value && sale?.customer?.id && await integrationsStore.ensureAstroEnabled()) {
      const resp = await IntegrationService.pointsStatus(sale.customer.id)
      const body = resp?.data?.body || {}
      const balance = body?.returnData?.points_balance
      if (typeof balance === 'number') {
        astroBalance.value = balance
      }
    }
  } catch {
    // ignore balance fetch errors for receipts
  }
})

const { sale } = defineProps({
  sale: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && typeof value === 'object' && 'id' in value
    }
  }
})

const saleCustomerDisplay = computed(() => resolveSaleCustomerDisplay(sale))
const saleCustomerReceiptLabel = computed(() =>
  resolveSaleCustomerReceiptLabel(sale, t('sales.saleReceiptWalkIn'))
)
const showCustomerContactPanel = computed(() =>
  saleHasAttachedCustomer(sale) && saleCustomerDisplay.value != null
)

const receiptVisiblePayments = computed(() => (sale.payments || []).filter(isReceiptPaymentVisible))
const primaryKlarnaPayment = computed(() =>
  receiptVisiblePayments.value.find(p => isKlarnaLikePayment(p)) || null
)
const showKlarnaPaymentDetails = computed(() =>
  primaryKlarnaPayment.value != null && sale.payment_method !== 'split'
)
const primaryCardPayment = computed(() =>
  receiptVisiblePayments.value.find(p => isCardLikePayment(p)) || null
)
const showCardPaymentDetails = computed(() =>
  !showKlarnaPaymentDetails.value
  && shouldShowCardPaymentDetails(primaryCardPayment.value, sale)
)
const primaryCashPayment = computed(() =>
  receiptVisiblePayments.value.find(p => isCashTenderPayment(p)) || null
)
const refundableCardPayments = computed(() =>
  receiptVisiblePayments.value.filter(p => isEligibleForProcessorRefund(p))
)

const splitConvenienceFeeTotal = computed(() => {
  if (sale.payment_method !== 'split') return 0
  const fromSale = Number(sale.convenience_fee_total || 0)
  if (fromSale > 0) return fromSale
  return receiptVisiblePayments.value.reduce((sum, p) => sum + Number(p?.fee_amount || 0), 0)
})

const showCashRoundingOnReceipt = computed(() => shouldShowCashRoundingOnReceipt(sale))
const receiptDisplayTotal = computed(() => getReceiptDisplayTotal(sale))
const cashRoundingAdjustmentDisplay = computed(() => (
  formatCashRoundingAdjustment(getSaleCashRoundingAdjustment(sale))
))

// Receipt should show precise transaction time
const { formatDate } = useDateFormatter('M/D/YYYY, h:mm:ss A')

function saleLineUnitDecimals (row) {
  const d = Number(row?.price_decimals ?? row?.product?.price_decimals)
  if (d === UNIT_DECIMALS_EXTENDED) return UNIT_DECIMALS_EXTENDED
  if (row?.product?.ring_by_amount) return UNIT_DECIMALS_EXTENDED
  return UNIT_DECIMALS_DEFAULT
}

function formatSaleLineUnitPrice (row) {
  const price = parseFloat(row.price) || 0
  return formatUnitPriceCurrency(price, saleLineUnitDecimals(row))
}

function lineItemLineTotal (row) {
  if (row.redeeming_for_points) {
    const totalCost = parseInt(row.total_redemption_cost) || 0
    return `${totalCost} pts`
  }
  const storedSubtotal = Number(row.subtotal)
  if (Number.isFinite(storedSubtotal)) {
    return `$${storedSubtotal.toFixed(2)}`
  }
  const price = parseFloat(row.price) || 0
  const quantity = Number(row.quantity) || 0
  let discount
  if (row.discount_type === 'percentage') {
    discount = (price * quantity) * (parseFloat(row.discount_amount) / 100)
  } else {
    discount = parseFloat(row.discount_amount) || 0
  }
  return `$${((price * quantity) - discount).toFixed(2)}`
}

const columns = computed(() => [
  {
    name: 'name',
    required: true,
    label: t('sales.saleReceiptTableProduct'),
    align: 'left',
    field: row => {
      const name = row.product_name || row.name || row.product?.name || 'Unknown Product'
      if (row.redeeming_for_points) {
        return `${name} (Redeemed)`
      }
      return name
    }
  },
  {
    name: 'quantity',
    required: true,
    label: t('sales.saleReceiptTableQty'),
    align: 'right',
    field: row => formatQuantityDisplay(row.quantity, {
      allowFractional: row.product?.allow_fractional ?? null
    })
  },
  {
    name: 'price',
    required: true,
    label: t('sales.saleReceiptTablePrice'),
    align: 'right',
    field: row => {
      if (row.redeeming_for_points) {
        const cost = parseInt(row.redemption_cost) || 0
        return `${cost} pts`
      }
      return formatSaleLineUnitPrice(row)
    }
  },
  {
    name: 'discount',
    label: t('sales.saleReceiptTableDiscount'),
    align: 'right',
    field: row => {
      if (row.redeeming_for_points) {
        return '-'
      }
      if (!(Number(row.discount_amount) > 0)) return '-'
      if (row.discount_type === 'percentage') {
        return `${formatQuantityDisplay(row.discount_amount, { allowFractional: true })}%`
      }
      return `$${Number(row.discount_amount).toFixed(2)}`
    }
  },
  {
    name: 'line_total',
    label: t('sales.saleReceiptTableLineTotal'),
    align: 'right',
    field: row => lineItemLineTotal(row)
  }
])

// Get redemption items for display
const redemptionItems = computed(() => {
  return (sale.items || []).filter(item => item.redeeming_for_points)
})

// Calculate total redemption points from items
const totalRedemptionPoints = computed(() => {
  return redemptionItems.value.reduce((total, item) => {
    return total + (parseInt(item.total_redemption_cost) || 0)
  }, 0)
})

const eventReceiptItem = computed(() => {
  const registrant = sale.event_registrant
  if (!registrant) return null
  const eventTitle = registrant.event?.title || 'Event'
  const packageName = registrant.ticket_package?.name
  const label = `Event Registration: ${eventTitle}${packageName ? ` (${packageName})` : ''}`
  const quantity = Number(registrant.quantity || 1)
  const amountPaid = Number(registrant.amount_paid || 0)
  const unitPrice = quantity > 0 ? (amountPaid / quantity) : amountPaid
  return {
    id: `event-registration-${registrant.id || 'sale'}`,
    name: label,
    quantity,
    price: unitPrice,
    total: amountPaid,
    is_event_registration: true
  }
})

const tabPaymentItemized = computed(() =>
  tabEntryReceiptLineItemsFromContext(sale.tab_payment_context).length > 0
)

const usesTabPaymentReceiptLayout = computed(() => {
  const ctx = sale.tab_payment_context
  if (!ctx) return false
  return isStructuredTabPaymentContext(ctx) || ctx.has_full_snapshot === false
})

const tabPaymentReceiptItem = computed(() => {
  const ctx = sale.tab_payment_context
  if (!ctx) return null
  const amountMinor = Number(ctx.payment_amount_minor || 0)
  const amount = amountMinor / 100
  return {
    id: `tab-payment-${ctx.tab_payment_id || ctx.tab_id || 'sale'}`,
    name: t('sales.tabPaymentReceipt.lineItem', { id: ctx.tab_id }),
    quantity: 1,
    price: amount,
    total: amount,
    subtotal: amount,
    is_tab_payment: true
  }
})

const tabPaymentReceiptItems = computed(() =>
  tabEntryReceiptLineItemsFromContext(sale.tab_payment_context)
)

const invoicePaymentReceiptItems = computed(() =>
  invoicePaymentReceiptLineItemsFromContext(sale.invoice_payment_context)
)

const itemsSectionTitle = computed(() => {
  if (tabPaymentItemized.value) return t('sales.tabPaymentReceipt.itemsTitle')
  if (invoicePaymentReceiptItems.value.length) return t('sales.invoicePaymentReceipt.itemsTitle')
  return t('sales.saleReceiptItems')
})

function receiptItemRowKey (row) {
  if (row.id != null && row.id !== '') {
    return String(row.id)
  }
  if (row.line_item_id != null) {
    return `line-${sale.id}-${row.line_item_id}`
  }
  return `receipt-row-${row.name ?? 'item'}-${row.quantity ?? 0}-${row.price ?? 0}`
}

const receiptItems = computed(() => {
  const items = Array.isArray(sale.items) ? [...sale.items] : []
  if (eventReceiptItem.value) {
    items.push(eventReceiptItem.value)
  }
  if (!usesTabPaymentReceiptLayout.value) {
    if (tabPaymentReceiptItems.value.length) {
      items.push(...tabPaymentReceiptItems.value)
    } else if (tabPaymentReceiptItem.value) {
      items.push(tabPaymentReceiptItem.value)
    }
  }
  if (invoicePaymentReceiptItems.value.length) {
    items.push(...invoicePaymentReceiptItems.value)
  }
  return items
})

const receiptTaxLines = computed(() => {
  return buildReceiptTaxDisplayLines(sale, { genericTaxLabel: t('sales.saleReceiptTax') })
})

// Helpers for per-payment action labels/eligibility
const isPaymentSameDay = (payment) => {
  if (!payment?.created_at) return false
  const created = new Date(payment.created_at)
  const now = new Date()
  return created.toDateString() === now.toDateString()
}

const getPaymentActionLabel = (payment) => {
  return isPaymentSameDay(payment)
    ? t('sales.saleReceiptVoidPayment')
    : t('sales.saleReceiptRefundPayment')
}

const confirmRefundOrVoid = async (payment) => {
  const isVoid = isPaymentSameDay(payment)
  const title = isVoid ? t('sales.saleReceiptConfirmVoidTitle') : t('sales.saleReceiptConfirmRefundTitle')
  const message = isVoid
    ? t('sales.saleReceiptConfirmVoidMessage')
    : t('sales.saleReceiptConfirmRefundMessage')

  bluDialog({
    title,
    message,
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      await paymentService.refundPayment(payment.id)
      Notify.create({
        type: 'positive',
        message: isVoid ? t('sales.saleReceiptVoidRequested') : t('sales.saleReceiptRefundRequested')
      })
    } catch (err) {
      console.error('Refund/Void failed', err)
      Notify.create({ type: 'negative', message: t('sales.saleReceiptRefundRequestFailed') })
    }
  })
}
</script>

<style lang="scss" scoped>
@import '../css/variables.scss';

.sale-receipt.receipt {
  max-width: 880px;
  margin: 0 auto;

  .redemption-row {
    background-color: #fff5f0;
    border-left: 3px solid $deep-orange;
  }

  .event-row {
    background-color: #f0f7ff;
    border-left: 3px solid $primary;
  }

  .tab-payment-row {
    background-color: #f5f3ff;
    border-left: 3px solid #7c3aed;
  }

  @media print {
    background: white;
    padding: 0;
    margin: 0;
    max-width: none;
    box-shadow: none !important;
    border: none !important;
    color: #000 !important;

    * {
      color: #000 !important;
      text-shadow: none !important;
    }

    .q-table {
      background: white !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      filter: none !important;
    }

    .q-table th,
    .q-table td,
    .q-table thead tr,
    .q-table tbody tr,
    .q-table__container {
      background: white !important;
      border-color: #ddd !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      filter: none !important;
    }

    .q-separator {
      background: #ddd;
    }

    .receipt-totals {
      padding: $spacing-sm $spacing-md !important;
      box-sizing: border-box;
    }

    .receipt-panel--sale-details {
      padding: $spacing-sm $spacing-md !important;
      box-sizing: border-box;
    }

    :deep(.q-dialog__backdrop),
    :deep(.q-dialog__actions) {
      display: none !important;
    }
  }
}

.receipt-hero {
  text-align: center;
}

.receipt-logo__img {
  max-width: 120px;
  max-height: 120px;
}

.receipt-panel {
  border-radius: $border-radius-md !important;
  background: #ffffff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid #e5e7eb !important;
  box-shadow: none !important;
  transition: none !important;

  &:hover {
    box-shadow: none !important;
    transform: none !important;
  }

  &--tint {
    background: #f4f6f9 !important;
    border-color: #e5e7eb !important;
  }
}

.receipt-panel__title {
  border-bottom: 1px solid #e5e7eb;
}

.receipt-kv {
  align-items: baseline;
}

.receipt-items-table {
  border-radius: $border-radius-md !important;
  overflow: hidden;

  :deep(thead tr) {
    background: #eef2f7;
  }

  :deep(th) {
    font-weight: $font-weight-medium;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #374151;
  }

  :deep(.q-table__bottom),
  :deep(.q-table__control) {
    display: none;
  }
}

.receipt-totals {
  border-radius: $border-radius-md !important;
  background: #ffffff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid #e5e7eb !important;
  box-shadow: none !important;
  transition: none !important;

  &:hover {
    box-shadow: none !important;
    transform: none !important;
  }
}

.receipt-redeemed-line {
  padding: 2px 0;
}

.receipt-payment-split {
  padding-left: 2px;
}

.receipt-payment-split__leg {
  border-top: 1px solid #e5e7eb;
}
</style>
